import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getEmployeeInfo,
    updateEmployeeAdvanced,
} from '@/modules/Employee/services'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
    EMPLOYEE_CONTRACT_TYPE,
    EMPLOYEE_SENIORITY_LEVEL,
    EMPLOYEE_EXTERNAL_TITLE,
    EMPLOYEE_CV_SOURCE,
} from '@/modules/Employee/constants'
import { GridHookForm, Loading } from 'vnetwork-ui'
import DepartmentPosition from './DepartmentPosition'
import { FilterSelect, FilterSelectAsync } from '@/components/ui/filters'
import { FormField } from '@/components/ui/form-field'
import { getCVs, getCVByID } from '@/modules/Resume/services'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { formatPGDate } from '@/utils/function'
import { getCurrentEmployee } from '@/modules/Account/components/services'
import CareerPath from '../CareerPath'

interface WorkInfoProps {
    employeeId?: number
    isSelf?: boolean
}

// Đã chọn nhóm nguồn thì phải nói rõ nguồn nào, trừ company_form (form cố định của công ty).
// .trim() vừa chặn gõ mỗi dấu cách, vừa chuẩn hoá giá trị trước khi gửi lên backend.
const cvSourceSchema = yup.object({
    employee_cv_source: yup.string().nullable(),
    employee_cv_source_detail: yup
        .string()
        .trim()
        .when('employee_cv_source', {
            is: (v: string) => !!v && v !== 'company_form',
            then: (s) => s.required('employee:cv_source_detail_required'),
            otherwise: (s) => s.nullable(),
        }),
})

// Tên CV bị trùng rất nhiều trong Talent Pool, nên nhãn phải kèm vị trí và ngày nộp.
const cvOptionLabel = (cv: any) =>
    [
        cv?.name,
        cv?.job_title,
        cv?.created_at && dayjs(cv.created_at).format('DD/MM/YYYY'),
    ]
        .filter(Boolean)
        .join(' — ')

const WorkInfo: React.FC<WorkInfoProps> = ({ employeeId, isSelf = false }) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () => (isSelf ? getCurrentEmployee() : getEmployeeInfo(id)),
        enabled: isSelf || !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: updateEmployeeMutation, isPending: isUpdatePending } =
        useMutationWithHandler(
            { mutationFn: updateEmployeeAdvanced },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEES,
                successMessage: 'employee-update-success',
                errorMessage: 'error-occurred',
            }
        )

    const formControl = useForm<any>({
        defaultValues: {
            employee_contract_type:
                (data?.result as any)?.employee_contract_type || '',
            employee_seniority_level:
                (data?.result as any)?.employee_seniority_level || '',
            employee_external_title:
                (data?.result as any)?.employee_external_title || '',
            employee_day_off_left: data?.result?.employee_day_off_left || 0,
            employee_cv_source: '',
            employee_cv_source_detail: '',
            employee_cv_cover_letter_id: null,
        },
        mode: 'onChange',
        resolver: yupResolver(cvSourceSchema) as any,
    })

    const isReadOnly = isSelf

    // Cờ ép FilterSelectAsync remount sau khi reset() đổ dữ liệu vào form.
    // Không có nó thì cờ `initialized` bên trong component khoá lại lúc value còn null,
    // getDefaultOptions không bao giờ chạy và ô picker hiện trắng.
    const [hydrated, setHydrated] = useState(false)

    // Ô picker gọi GET /cv, gate bằng Resumes:index — khác resource với trang này (Employees).
    const { permissions, isAdmin } = usePermissionsStore()
    const canReadResumes = useMemo(() => {
        if (isAdmin) return true
        const granted =
            permissions.find((p: any) => p?.rsname === resources.resume)
                ?.scopes || []
        return granted.includes(scopes.index)
    }, [isAdmin, permissions])

    // Reset form when data loads (defaultValues only apply on first render)
    useEffect(() => {
        if (!data?.result) return
        formControl.reset({
            employee_start_date:
                dayjs(data.result.employee_start_date, 'YYYY-MM-DD') || null,
            employee_end_date: data.result.employee_end_date
                ? dayjs(data.result.employee_end_date, 'YYYY-MM-DD')
                : null,
            employee_day_off_left: data.result.employee_day_off_left || 0,
            employee_contract_type:
                (data.result as any)?.employee_contract_type || '',
            employee_seniority_level:
                (data.result as any)?.employee_seniority_level || '',
            employee_external_title:
                (data.result as any)?.employee_external_title || '',
            // Hai ô text phải là chuỗi rỗng, không được null: input spread {...field} trần
            // nên null sẽ làm React cảnh báo và ô nhảy uncontrolled -> controlled.
            employee_cv_source: (data.result as any)?.employee_cv_source ?? '',
            employee_cv_source_detail:
                (data.result as any)?.employee_cv_source_detail ?? '',
            // Ô picker đổ số hoặc null. Đừng đổ 0 — 0 là tín hiệu xoá liên kết.
            employee_cv_cover_letter_id:
                (data.result as any)?.employee_cv_cover_letter_id ?? null,
        })
        setHydrated(true)
    }, [data?.result])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-6">
            {/* Department & Position */}
            <DepartmentPosition employee={data?.result} />

            {/* Day off left */}
            <GridHookForm
                actions={
                    isReadOnly
                        ? []
                        : [
                              {
                                  title: t('save-changes'),
                                  type: 'submit',
                                  variant: 'contained',
                                  loading: isUpdatePending,
                                  disabled:
                                      isUpdatePending ||
                                      !formControl.formState.isDirty,
                              },
                          ]
                }
                formControl={formControl}
                onSubmit={(values) => {
                    if (isReadOnly) return

                    // FilterSelectAsync ghi CẢ object {label, value} vào form, bấm X thì ghi null.
                    // Backend cần một số: 0 = xoá liên kết, còn null bị hiểu là "giữ nguyên".
                    const cvRaw = (values as any).employee_cv_cover_letter_id
                    const cvId =
                        cvRaw && typeof cvRaw === 'object'
                            ? Number((cvRaw as any).value)
                            : cvRaw == null
                              ? 0
                              : Number(cvRaw)

                    updateEmployeeMutation({
                        ...values,
                        employee_cv_cover_letter_id: cvId,
                        employee: {
                            employee_day_off_left:
                                values?.employee_day_off_left,
                            employee_end_date: values.employee_end_date
                                ? formatPGDate(values.employee_end_date)
                                : null,
                            employee_start_date: formatPGDate(
                                values.employee_start_date
                            ),
                            employee_contract_type:
                                values.employee_contract_type,
                            employee_seniority_level:
                                values.employee_seniority_level,
                            employee_external_title:
                                values.employee_external_title,
                        },
                        employee_name: data?.result?.employee_name,
                        employee_id: Number(id),
                    })
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data={[
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_contract_type"
                                label={t('employee_contract_type')}
                                placeholder={t(
                                    'employee_contract_type_placeholder'
                                )}
                                options={EMPLOYEE_CONTRACT_TYPE(t)}
                                showClearButton={false}
                                disabled={isReadOnly}
                            />
                        ),
                        className: 'md:col-span-1',
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_seniority_level"
                                label={t('employee_seniority_level')}
                                placeholder={t(
                                    'employee_seniority_level_placeholder'
                                )}
                                options={EMPLOYEE_SENIORITY_LEVEL}
                                showClearButton={false}
                                disabled={isReadOnly}
                            />
                        ),
                        className: 'md:col-span-1',
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_external_title"
                                label={t('employee_external_title')}
                                placeholder={t('employee_external_title')}
                                options={EMPLOYEE_EXTERNAL_TITLE}
                                disabled={isReadOnly}
                            />
                        ),
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            type: 'datepicker',
                            name: 'employee_start_date',
                            placeholder: t('employee_start_date'),
                            label: t('employee_start_date'),
                            disabled: isReadOnly,
                        },
                        className: 'md:col-span-1',
                    },
                    {
                        formItemProps: {
                            type: 'datepicker',
                            name: 'employee_end_date',
                            placeholder: t('employee_end_date'),
                            disabled:
                                isReadOnly ||
                                data?.result?.employee_status === 1,
                            label: t('employee_end_date'),
                        },
                        className: 'md:col-span-1',
                    },
                    {
                        formItemProps: {
                            type: 'number',
                            name: 'employee_day_off_left',
                            placeholder: t('employee_day_off_left'),
                            label: t('employee_day_off_left'),
                            disabled: isReadOnly,
                        },
                        className: 'md:col-span-1',
                    },
                    {
                        renderEditComponent: (control) => (
                            <FilterSelect
                                control={control}
                                name="employee_cv_source"
                                label={t('employee:cv_source')}
                                placeholder={t('employee:cv_source')}
                                options={EMPLOYEE_CV_SOURCE(t)}
                                disabled={isReadOnly}
                                onChangeAfterHookForm={() => {
                                    // shouldValidate là bắt buộc: RHF chỉ ghi lỗi cho field vừa đổi,
                                    // thiếu cờ này thì đổi nhóm sẽ không hiện chữ đỏ nào.
                                    formControl.setValue(
                                        'employee_cv_source_detail',
                                        '',
                                        { shouldValidate: true }
                                    )
                                }}
                            />
                        ),
                        className: 'md:col-span-1',
                    },
                    {
                        // Bắt buộc FormField của repo: chỉ nó dịch chuỗi lỗi qua t(rawError).
                        renderEditComponent: (control) => (
                            <FormField
                                control={control}
                                name="employee_cv_source_detail"
                                type="textfield"
                                label={t('employee:cv_source_detail')}
                                placeholder={t('employee:cv_source_detail')}
                                disabled={isReadOnly}
                            />
                        ),
                        className: 'md:col-span-1',
                    },
                    // Bỏ hẳn phần tử khi thiếu quyền, không dùng prop hidden:
                    // GridHookForm vẫn render div bọc nên trả null sẽ để lại một ô lưới rỗng.
                    ...(canReadResumes
                        ? [
                              {
                                  renderEditComponent: (control: any) => (
                                      <FilterSelectAsync
                                          key={
                                              hydrated ? 'cv-ready' : 'cv-init'
                                          }
                                          control={control}
                                          name="employee_cv_cover_letter_id"
                                          label={t(
                                              'employee:cv_source_cover_letter'
                                          )}
                                          placeholder={t(
                                              'employee:cv_source_cover_letter'
                                          )}
                                          disabled={isReadOnly}
                                          minSearchLength={3}
                                          asyncOptions={async (q: string) => {
                                              const res = await getCVs({
                                                  page: 1,
                                                  page_size: 50,
                                                  q,
                                              })
                                              // ResponseArray => results (số nhiều).
                                              return (res?.results || []).map(
                                                  (cv: any) => ({
                                                      label: cvOptionLabel(cv),
                                                      value: cv.id,
                                                  })
                                              )
                                          }}
                                          getDefaultOptions={async (
                                              currentValue: any
                                          ) => {
                                              const cvId =
                                                  typeof currentValue ===
                                                  'object'
                                                      ? currentValue?.value
                                                      : currentValue
                                              if (!cvId) return []
                                              // ResponseObject => result (số ít).
                                              const cv = (
                                                  await getCVByID(Number(cvId))
                                              )?.result
                                              return cv
                                                  ? [
                                                        {
                                                            label: cvOptionLabel(
                                                                cv
                                                            ),
                                                            value: cv.id,
                                                        },
                                                    ]
                                                  : []
                                          }}
                                      />
                                  ),
                                  className: 'md:col-span-2',
                              },
                          ]
                        : []),
                ]}
            />

            {/* Career Path */}
            <div className="border-t border-gray-200 pt-4">
                <CareerPath employeeId={employeeId} isSelf={isSelf} />
            </div>
        </div>
    )
}

export default WorkInfo
